import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';

interface SettlementSidebarProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  onHideBar?: () => void;
  filteredRows: any[];
  dateRangeStart: string;
  dateRangeEnd: string;
}

const sidebarHeight = 500; // 사이드바 높이
const closedHeight = 48; // 닫힌 상태 높이 (버튼만 보이는 높이)

function formatNumberComma(val: any) {
  if (val === undefined || val === null || val === '') return '';
  const num = Number(String(val).replace(/[^0-9.-]/g, ''));
  if (isNaN(num)) return val;
  return num.toLocaleString('ko-KR');
}

// 통합일보/정산일보와 동일한 방식의 방어적 숫자 변환 함수
function safeToNumber(val: any): number {
  if (Array.isArray(val)) val = val[0];
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'string') {
    val = val.replace(/[^0-9.-]/g, '');
  }
  const num = Number(val);
  return isNaN(num) ? 0 : num;
}

const SettlementSidebar: React.FC<SettlementSidebarProps> = ({ open, onClose, onOpen, onHideBar, filteredRows, dateRangeStart, dateRangeEnd }) => {
  const [currentHeight, setCurrentHeight] = useState(sidebarHeight);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const startHeight = useRef(0);

  // 리사이징 시작
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    startY.current = e.clientY;
    startHeight.current = currentHeight;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  // 리사이징 중
  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaY = startY.current - e.clientY; // 위로 드래그하면 양수
    const newHeight = Math.max(
      closedHeight + 50, // 최소 높이
      Math.min(
        window.innerHeight * 0.8, // 최대 높이 (화면의 80%)
        startHeight.current + deltaY
      )
    );
    setCurrentHeight(newHeight);
  };

  // 리사이징 종료
  const handleMouseUp = () => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // 전역 마우스 이벤트 리스너
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  // 사이드바가 닫힐 때 높이 초기화
  useEffect(() => {
    if (!open) {
      setCurrentHeight(sidebarHeight);
    }
  }, [open]);
  // 청구운송비 합계
  const totalBilling = filteredRows.reduce((sum, r) => sum + safeToNumber(r.billingAmount), 0);
  // 지불운송비 총계 (0보다 큰 값만)
  const totalPayment = filteredRows.filter(r => safeToNumber(r.paymentAmount) > 0).reduce((sum, r) => sum + safeToNumber(r.paymentAmount), 0);
  // 미지급금: 지불운송비가 0보다 크고 미정산 상태인 행의 paymentAmount 합계
  const unpaid = filteredRows
    .filter(r => safeToNumber(r.paymentAmount) > 0 && r.settlementStatus !== '정산')
    .reduce((sum, r) => sum + safeToNumber(r.paymentAmount), 0);

  // 거래처별 청구운송비 합계 (오름차순 정렬)
  const billingByCompany: Record<string, number> = {};
  filteredRows.forEach(r => {
    if (!r.company) return;
    billingByCompany[r.company] = (billingByCompany[r.company] || 0) + safeToNumber(r.billingAmount);
  });

  // 소속별 지불운송비 합계 (오름차순 정렬)
  const paymentByDepartment: Record<string, number> = {};
  filteredRows.forEach(r => {
    if (!r.department) return;
    paymentByDepartment[r.department] = (paymentByDepartment[r.department] || 0) + safeToNumber(r.paymentAmount);
  });

  // 0보다 큰 값만 합산한 소속별 지불운송비 총계
  const totalPositivePayment = Object.values(paymentByDepartment).filter(v => v > 0).reduce((sum, v) => sum + v, 0);

  // 정산/미정산 건수
  const settledCount = filteredRows.filter(r => r.settlementStatus === '정산').length;
  const unsettledCount = filteredRows.filter(r => r.settlementStatus === '미정산').length;

  // 차량번호별 지불운송비 합계 (오름차순 정렬)
  const paymentByVehicle: Record<string, number> = {};
  filteredRows.forEach(r => {
    if (!r.vehicleNumber) return;
    paymentByVehicle[r.vehicleNumber] = (paymentByVehicle[r.vehicleNumber] || 0) + safeToNumber(r.paymentAmount);
  });

  // 엑셀 다운로드 핸들러
  const handleDownloadExcel = () => {
    // 1. 거래처별 청구운송비
    const companyRows = [
      ['거래처상호', '청구운송비'],
      ...Object.entries(billingByCompany).map(([company, sum]) => [company, sum]),
      ['총계', totalBilling]
    ];
    // 2. 소속별 지불운송비
    const deptRows = [
      ['소속', '지불운송비'],
      ...Object.entries(paymentByDepartment).map(([dept, sum]) => [dept, sum]),
      ['총계', totalPositivePayment]
    ];
    // 3. 차량번호별 지불운송비
    const vehicleRows = [
      ['차량번호', '지불운송비'],
      ...Object.entries(paymentByVehicle).map(([vehicle, sum]) => [vehicle, sum]),
      ['총계', Object.values(paymentByVehicle).reduce((sum, v) => sum + v, 0)]
    ];
    // 4. 워크북/시트 생성
    const wb = XLSX.utils.book_new();
    const companySheet = XLSX.utils.aoa_to_sheet(companyRows);
    const deptSheet = XLSX.utils.aoa_to_sheet(deptRows);
    const vehicleSheet = XLSX.utils.aoa_to_sheet(vehicleRows);
    // 5. 숫자 서식 적용 (두 번째 컬럼 전체)
    [companySheet, deptSheet, vehicleSheet].forEach(sheet => {
      const range = XLSX.utils.decode_range(sheet['!ref'] || '');
      for (let r = 1; r <= range.e.r; r++) { // r=1부터: 헤더 제외
        const cell = sheet[XLSX.utils.encode_cell({ c: 1, r })];
        if (cell) cell.z = '#,##0';
      }
    });
    XLSX.utils.book_append_sheet(wb, companySheet, '거래처별 청구운송비');
    XLSX.utils.book_append_sheet(wb, deptSheet, '소속별 지불운송비');
    XLSX.utils.book_append_sheet(wb, vehicleSheet, '차량번호별 지불운송비');
    // 6. 파일 다운로드
    XLSX.writeFile(wb, `정산통계_${dateRangeStart}~${dateRangeEnd}.xlsx`);
  };

  return (
    <div
      ref={sidebarRef}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100vw',
        height: open ? currentHeight : closedHeight,
        background: 'linear-gradient(135deg, #f8fafc 0%, #e3e8ef 100%)',
        boxShadow: open ? '0 -4px 16px rgba(0,0,0,0.10)' : '0 -2px 8px rgba(0,0,0,0.05)',
        zIndex: 1200,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        overflow: 'hidden',
        transition: isResizing ? 'none' : 'height 0.3s ease-in-out',
      }}
    >
      {open ? (
        <>
          {/* 드래그 핸들 */}
          <div
            onMouseDown={handleMouseDown}
            style={{
              height: 20,
              width: '100%',
              cursor: 'ns-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isResizing ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
              borderBottom: '1px solid #e0e6ed',
              position: 'relative',
            }}
            title="드래그하여 크기 조정"
          >
            <div style={{
              width: 60,
              height: 4,
              background: '#bdbdbd',
              borderRadius: 2,
              transition: 'background 0.2s'
            }} />
          </div>
          
                     {/* 접기 버튼 */}
           <button
             onClick={onClose}
             style={{
               position: 'absolute',
               left: '50%',
               transform: 'translateX(-50%)',
               top: 24,
               width: 48,
               height: 32,
               background: '#1976d2',
               color: '#fff',
               border: 'none',
               borderRadius: '12px 12px 0 0',
               fontSize: 18,
               fontWeight: 700,
               cursor: 'pointer',
               boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
               zIndex: 1500,
             }}
             title="사이드바 접기"
           >▼</button>
          <div style={{ 
            padding: '60px 24px 24px 24px', // 상단 패딩을 늘려서 드래그 핸들과 접기 버튼 공간 확보
            flex: 1, 
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            scrollbarColor: '#9e9e9e #f5f5f5'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 0, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: '#1976d2' }}>정산 통계</h3>
              <span style={{ fontSize: 16, color: '#222', fontWeight: 700, letterSpacing: 1 }}>{dateRangeStart} ~ {dateRangeEnd}</span>
              {/* 엑셀 다운로드 버튼 */}
              <button
                onClick={handleDownloadExcel}
                style={{ marginLeft: 'auto', background: '#43a047', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
              >엑셀 다운로드</button>
            </div>
            {/* 카드형 요약 */}
            <div style={{ display: 'flex', gap: 12, margin: '18px 0 24px 0', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ background: '#fff3e0', borderRadius: 8, padding: '14px 22px', fontWeight: 700, fontSize: 17, color: '#d32f2f', minWidth: 140, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                청구운송비<br /><span style={{ fontSize: 22 }}>{formatNumberComma(totalBilling)}<span style={{ fontWeight: 400, fontSize: 15, color: '#bdbdbd' }}>원</span></span>
              </div>
              <div style={{ background: '#e3f2fd', borderRadius: 8, padding: '14px 22px', fontWeight: 700, fontSize: 17, color: '#1976d2', minWidth: 140, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                지불운송비<br /><span style={{ fontSize: 22 }}>{formatNumberComma(totalPayment)}<span style={{ fontWeight: 400, fontSize: 15, color: '#bdbdbd' }}>원</span></span>
              </div>
              <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '14px 22px', fontWeight: 700, fontSize: 17, color: '#333', minWidth: 140, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                미지급금<br /><span style={{ fontSize: 22 }}>{formatNumberComma(unpaid)}<span style={{ fontWeight: 400, fontSize: 15, color: '#bdbdbd' }}>원</span></span>
              </div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '14px 18px', fontWeight: 700, fontSize: 17, color: '#d32f2f', minWidth: 90, textAlign: 'center', border: '2px solid #d32f2f' }}>
                정산<br /><span style={{ fontSize: 22 }}>{settledCount}<span style={{ fontWeight: 400, fontSize: 15, color: '#bdbdbd' }}>건</span></span>
              </div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '14px 18px', fontWeight: 700, fontSize: 17, color: '#1976d2', minWidth: 90, textAlign: 'center', border: '2px solid #1976d2' }}>
                미정산<br /><span style={{ fontSize: 22 }}>{unsettledCount}<span style={{ fontWeight: 400, fontSize: 15, color: '#bdbdbd' }}>건</span></span>
              </div>
            </div>
            <hr style={{ border: 'none', borderTop: '1.5px solid #e0e6ed', margin: '0 0 20px 0' }} />
            {/* 3개 컬럼 레이아웃 */}
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {/* 거래처별 청구운송비 피벗 */}
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#c2185b', marginBottom: 8 }}>거래처별 청구운송비</div>
              <table style={{ width: '100%', fontSize: 15, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', color: '#c2185b' }}>거래처상호</th>
                    <th style={{ textAlign: 'right', color: '#c2185b' }}>청구운송비</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(billingByCompany)
                    .sort((a, b) => a[0].localeCompare(b[0], 'ko'))
                    .map(([company, sum], idx, arr) => (
                      <tr key={company}
                        style={idx === arr.length - 1 ? { borderBottom: '2px solid #bbb' } : {}}>
                        <td>{company}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumberComma(sum)}원</td>
                      </tr>
                    ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>총계</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatNumberComma(totalBilling)}원</td>
                  </tr>
                </tbody>
                </table>
              </div>
              {/* 소속별 지불운송비 피벗 */}
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#1976d2', marginBottom: 8 }}>소속별 지불운송비</div>
              <table style={{ width: '100%', fontSize: 15, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', color: '#1976d2' }}>소속</th>
                    <th style={{ textAlign: 'right', color: '#1976d2' }}>지불운송비</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(paymentByDepartment)
                    .filter(([, sum]) => sum > 0)
                    .sort((a, b) => a[0].localeCompare(b[0], 'ko'))
                    .map(([dept, sum], idx, arr) => (
                      <tr key={dept}
                        style={idx === arr.length - 1 ? { borderBottom: '2px solid #bbb' } : {}}>
                        <td>{dept}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumberComma(sum)}원</td>
                      </tr>
                    ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>총계</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatNumberComma(totalPositivePayment)}원</td>
                  </tr>
                </tbody>
                </table>
              </div>
              {/* 차량번호별 지불운송비 피벗 */}
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#1976d2', marginBottom: 8 }}>차량번호별 지불운송비</div>
              <table style={{ width: '100%', fontSize: 15, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', color: '#1976d2' }}>차량번호</th>
                    <th style={{ textAlign: 'right', color: '#1976d2' }}>지불운송비</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(paymentByVehicle)
                    .filter(([, sum]) => sum > 0)
                    .sort((a, b) => a[0].localeCompare(b[0], 'ko'))
                    .map(([vehicle, sum], idx, arr) => (
                      <tr key={vehicle}
                        style={idx === arr.length - 1 ? { borderBottom: '2px solid #bbb' } : {}}>
                        <td>{vehicle}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumberComma(sum)}원</td>
                      </tr>
                    ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>총계</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatNumberComma(Object.values(paymentByVehicle).filter(v => v > 0).reduce((sum, v) => sum + v, 0))}원</td>
                  </tr>
                </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          position: 'relative'
        }}>
          <button
            onClick={onOpen}
            style={{
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: '12px 12px 0 0',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
              zIndex: 1500,
              padding: '8px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 0
            }}
            title="정산 통계 보기"
          >
            <span>정산 통계</span>
            <span>▲</span>
          </button>
          {onHideBar && (
            <button
              type="button"
              onClick={onHideBar}
              style={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                fontSize: 20,
                fontWeight: 700,
                color: '#666',
                cursor: 'pointer',
                padding: 4,
                lineHeight: 1
              }}
              title="정산 통계 숨기기"
            >
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SettlementSidebar; 